package top.wcpe.allincore.ui.runtime.ir

import kotlin.jvm.JvmStatic
import kotlin.math.floor

/**
 * DSL 运行期值的纯函数工具集。
 *
 * 所有方法均为无副作用、无依赖的工具函数，仅消费输入对象返回新值或描述字符串。
 * 抽出本类的目的是打破 [ExprEvaluator] 与拆分子类之间的同包静态环依赖，
 * 便于后续把 ExprEvaluator 抽接口、迁子包或并行多解释器实现。
 */
object DslValueUtils {

    /** 相等判定：null 等价；Number 跨子类按 double 比较；其余按 equals。 */
    @JvmStatic
    fun equalsValue(l: Any?, r: Any?): Boolean {
        if (l == null || r == null) return l === r
        if (l is Number && r is Number) return l.toDouble() == r.toDouble()
        return l == r
    }

    /** 真值判断：null 视为 false；Boolean 直接取值；其余视为 true。 */
    @JvmStatic
    fun truthy(v: Any?): Boolean {
        if (v == null) return false
        if (v is Boolean) return v
        return true
    }

    /** 取对象的类型描述，null 安全；用于错误消息。KMP 迁移后改 KClass.simpleName（JS 无 javaClass），输出为短类名。 */
    @JvmStatic
    fun classOf(v: Any?): String = if (v == null) "null" else v::class.simpleName ?: "?"

    /**
     * DSL 值的字符串化：null → "null"；Double 整数值（含 5.0 → "5"）转为整数形式，
     * 避免插值显示带小数尾巴；Map/List 递归字符串化（保持 `{k=v, ...}` / `[a, b]` 的 toString 观感，
     * 但内部数字同样整数化，避免 `${整个Map}` 插值出 `{idx=2.0}`）；其余走 [Any.toString]。
     *
     * 与 [top.wcpe.allincore.json.Json] 中 Double 整数化策略保持一致；区别是 Json 输出 JSON（带引号），
     * 本方法面向插值文案、保留 Java 容器 toString 的无引号观感。
     */
    @Suppress("ComplexCondition")
    @JvmStatic
    fun stringify(v: Any?): String {
        if (v == null) return "null"
        if (v is Double) {
            if (!v.isNaN() &&
                !v.isInfinite() &&
                v == floor(v) &&
                v >= Long.MIN_VALUE &&
                v <= Long.MAX_VALUE
            ) {
                return v.toLong().toString()
            }
        }
        // Map/List 递归整数化：复刻 AbstractMap/AbstractCollection 的 toString 观感（"{k=v, ...}" / "[a, b]"），
        // 但每个元素改走本方法递归，使容器内嵌的整数 Double 也输出为整数（原走 v.toString() 时内部 Double 仍是 "2.0"）。
        if (v is Map<*, *>) {
            val sb = StringBuilder("{")
            var first = true
            for ((k, value) in v) {
                if (!first) sb.append(", ")
                first = false
                sb.append(stringify(k)).append('=').append(stringify(value))
            }
            return sb.append('}').toString()
        }
        if (v is List<*>) {
            val sb = StringBuilder("[")
            var first = true
            for (item in v) {
                if (!first) sb.append(", ")
                first = false
                sb.append(stringify(item))
            }
            return sb.append(']').toString()
        }
        return v.toString()
    }
}
